import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import logger from '../utils/logger';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await api.get('/agency/verify');
      if (response.data.success) {
        setUser(response.data.user);
        setAgency(response.data.agency);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        // Cookie cleared server-side on 401
      }
      logger.error('Auth check error:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await api.post('/agency/login', { email, password });

    if (response.data.success) {
      if (response.data.token) {
        localStorage.setItem('agencyToken', response.data.token);
      }
      setUser(response.data.user);
      setAgency(response.data.agency);
      return { success: true };
    }

    return { success: false, message: response.data.message };
  };

  const register = async (data) => {
    const response = await api.post('/agency/register', data);

    if (response.data.success) {
      if (response.data.token) {
        localStorage.setItem('agencyToken', response.data.token);
      }
      setUser(response.data.user);
      setAgency(response.data.agency);
      return { success: true };
    }

    return { success: false, message: response.data.message };
  };

  const logout = async () => {
    try {
      await api.post('/agency/logout');
    } catch (error) {
      // Continue with logout even if API fails
    }

    localStorage.removeItem('agencyToken');
    setUser(null);
    setAgency(null);
    navigate('/login');
  };

  const updateAgency = (updatedAgency) => {
    setAgency((prev) => ({ ...prev, ...updatedAgency }));
  };

  const value = {
    user,
    agency,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    updateAgency,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
